(ns io.daskul.webapp.widgets.form.activity
  "Activity timeline widget for aggregate sidebar.

   Fetches GET /<resource>/:id/history and renders a unified timeline
   of FSM transitions and manual notes, sorted newest-first.

   Reuses the collection endpoints for note add/delete:
   - POST /<resource>/:id/<collection-field> to append a note
   - DELETE /<resource>/:id/<collection-field>/:entry-id to remove"
  (:require
   [uix.core :as uix :refer [defui $]]
   [re-frame.core :as rf]
   [ajax.core :as ajax]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [uix.re-frame :as urf]))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn- format-relative
  "Format an ISO timestamp as relative time (e.g. '2h ago', '3d ago')."
  [iso-string]
  (when iso-string
    (try
      (let [date (js/Date. iso-string)
            now (js/Date.)
            diff-ms (- (.getTime now) (.getTime date))
            diff-mins (/ diff-ms 1000 60)
            diff-hours (/ diff-mins 60)
            diff-days (/ diff-hours 24)]
        (cond
          (< diff-mins 1) "just now"
          (< diff-mins 60) (str (js/Math.floor diff-mins) "m ago")
          (< diff-hours 24) (str (js/Math.floor diff-hours) "h ago")
          (< diff-days 30) (str (js/Math.floor diff-days) "d ago")
          :else (.toLocaleDateString date "en-US" #js {:month "short" :day "numeric"})))
      (catch :default _ iso-string))))

(defn- format-param-value
  "Format a param value for display. Dates get short month+day."
  [value]
  (if (and (string? value) (re-matches #"\d{4}-\d{2}-\d{2}" value))
    (try
      (.toLocaleDateString (js/Date. (str value "T00:00:00"))
                           "en-US" #js {:month "short" :day "numeric"})
      (catch :default _ value))
    (str value)))

(defn- history-url
  "Build the history endpoint URL for an aggregate record."
  [agg-key id]
  (let [resource (agg.events/get-resource-name agg-key)]
    (str (agg.events/get-api-base) "/" resource "/" id "/history")))

(defn- collection-url
  "Build the collection endpoint URL for note operations."
  [agg-key id field-name]
  (let [resource (agg.events/get-resource-name agg-key)]
    (str (agg.events/get-api-base) "/" resource "/" id "/" (name field-name))))

;; ============================================================================
;; HTTP operations
;; ============================================================================

(defn- fetch-history!
  "Fetch timeline entries from the history endpoint."
  [agg-key id on-success]
  (ajax/GET (history-url agg-key id)
    {:headers (agg.events/get-auth-header)
     :response-format (ajax/json-response-format {:keywords? true})
     :handler on-success
     :error-handler (fn [err]
                      (js/console.error "Failed to fetch history" err))}))

(defn- append-note!
  "POST a new note to the collection endpoint."
  [agg-key id field-name text on-done]
  (let [url (collection-url agg-key id field-name)]
    (ajax/POST url
      {:params {:text text}
       :headers (agg.events/get-auth-header)
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :handler (fn [_] (on-done))
       :error-handler (fn [err]
                        (js/console.error "Failed to append note" err))})))

(defn- delete-note!
  "DELETE a note from the collection endpoint."
  [agg-key id field-name entry-id on-done]
  (let [url (str (collection-url agg-key id field-name) "/" entry-id)]
    (ajax/DELETE url
      {:headers (agg.events/get-auth-header)
       :response-format (ajax/json-response-format {:keywords? true})
       :handler (fn [_] (on-done))
       :error-handler (fn [err]
                        (js/console.error "Failed to delete note" err))})))

;; ============================================================================
;; Transition params rendering
;; ============================================================================

(defn- group-params
  "Group params by :inline_group. Returns {:grouped [[group-num [params...]]] :ungrouped [params...]}.
   Params with non-nil :inline_group are grouped together; others are ungrouped."
  [params]
  (let [{grouped true ungrouped false}
        (group-by #(some? (:inline_group %)) params)]
    {:grouped (->> grouped
                   (group-by :inline_group)
                   (sort-by first)
                   vec)
     :ungrouped (vec ungrouped)}))

(defui transition-params
  "Render transition params with inline grouping."
  [{:keys [params]}]
  (when (seq params)
    (let [{:keys [grouped ungrouped]} (group-params params)]
      ($ ui/stack {:gap 2 :mt "xs"}
        (for [[group-num group-params] grouped]
          ($ ui/text {:key (str "g-" group-num) :size "sm" :c "dimmed"}
            (->> group-params
                 (map #(str (:label %) ": " (format-param-value (:value %))))
                 (interpose " \u00B7 ")
                 (apply str))))
        (for [param ungrouped]
          ($ ui/text {:key (:label param) :size "sm" :c "dimmed"}
            (str (:label param) ": " (format-param-value (:value param)))))))))

;; ============================================================================
;; Timeline entry components
;; ============================================================================

(defui transition-entry
  "Render a single transition timeline entry."
  [{:keys [entry]}]
  (let [{:keys [label from_state to_state params note actor occurred_at]} entry]
    ($ ui/box {:pb "sm" :mb "sm"
               :style {:borderBottom "1px solid var(--mantine-color-gray-2)"}}
      ;; Label (bold)
      ($ ui/group {:gap 6 :align "center"}
        ($ icons/icon-refresh {:size 14 :color "var(--mantine-color-dimmed)"})
        ($ ui/text {:size "sm" :fw 600} label))
      ;; From -> To
      (when (and from_state to_state)
        ($ ui/text {:size "xs" :c "dimmed" :mt 2}
          (str (:label from_state) " \u2192 " (:label to_state))))
      ;; Params
      ($ transition-params {:params params})
      ;; Note (if present)
      (when (seq note)
        ($ ui/text {:size "sm" :mt "xs"} note))
      ;; Actor + time
      ($ ui/text {:size "xs" :c "dimmed" :mt "xs"}
        (str (when actor (str actor " \u00B7 "))
             (format-relative occurred_at))))))

(defui note-entry
  "Render a single note timeline entry with hover delete."
  [{:keys [entry on-delete]}]
  (let [{:keys [id text date by state]} entry
        [hovered? set-hovered!] (uix/use-state false)
        [confirm? set-confirm!] (uix/use-state false)
        handle-delete (uix/use-callback
                       (fn []
                         (set-confirm! false)
                         (on-delete id))
                       [on-delete id])]
    ($ ui/box {:onMouseEnter #(set-hovered! true)
               :onMouseLeave #(do (set-hovered! false) (set-confirm! false))
               :pb "sm" :mb "sm"
               :style {:borderBottom "1px solid var(--mantine-color-gray-2)"
                       :borderRadius 4
                       :padding "6px 6px 12px 6px"
                       :backgroundColor (when hovered? "var(--mantine-color-gray-0)")
                       :transition "background-color 150ms ease"}}
      ;; Note text
      ($ ui/multiline-text {:size "sm"} text)
      ;; State + by + time
      ($ ui/group {:justify "space-between" :mt "xs"}
        ($ ui/group {:gap 6}
          (when state
            ($ ui/text {:size "xs" :c "dimmed"} (:label state)))
          (when by
            ($ ui/text {:size "xs" :c "dimmed"} by))
          (when date
            ($ ui/text {:size "xs" :c "dimmed"} (format-relative date))))
        ;; Delete with confirmation popover
        (when on-delete
          ($ ui/popover {:opened confirm? :position "bottom-end" :withArrow true
                         :onClose #(set-confirm! false)}
            ($ ui/popover-target
              ($ ui/action-icon
                {:size "xs" :variant "subtle" :color "red"
                 :style {:opacity (if hovered? 1 0)
                         :transition "opacity 150ms ease"}
                 :onClick #(set-confirm! true)}
                ($ icons/icon-trash {:size 12})))
            ($ ui/popover-dropdown
              ($ ui/stack {:gap "xs"}
                ($ ui/text {:size "xs"} "Delete this note?")
                ($ ui/group {:gap "xs"}
                  ($ ui/button {:size "compact-xs" :variant "light" :color "gray"
                                :onClick #(set-confirm! false)} "Cancel")
                  ($ ui/button {:size "compact-xs" :color "red"
                                :onClick handle-delete}
                    "Delete"))))))))))

;; ============================================================================
;; Main widget
;; ============================================================================

(defui activity-widget
  "Activity timeline widget for aggregate sidebar.

   Props:
   - agg-key: keyword - aggregate key (e.g. :lead)
   - record-id: uuid - record being edited
   - collection-field: keyword or nil - collection field name for notes (e.g. :notes)"
  [{:keys [agg-key record-id collection-field]}]
  (let [record (urf/use-subscribe [::agg.subs/by-id agg-key record-id])
        record-state (:state record)
        record-updated (:updated-at record)
        [entries set-entries!] (uix/use-state nil)
        [loading? set-loading!] (uix/use-state true)
        [text set-text!] (uix/use-state "")
        [adding? set-adding!] (uix/use-state false)

        do-fetch!
        (uix/use-callback
         (fn []
           (set-loading! true)
           (fetch-history! agg-key record-id
                           (fn [response]
                             (set-entries! response)
                             (set-loading! false))))
         [agg-key record-id])

        handle-add
        (uix/use-callback
         (fn []
           (when (and collection-field (seq (.trim text)))
             (set-adding! true)
             (append-note! agg-key record-id collection-field (.trim text)
                           (fn []
                             (set-text! "")
                             (set-adding! false)
                             (do-fetch!)
                             (rf/dispatch [::agg.events/get agg-key record-id])))))
         [agg-key record-id collection-field text do-fetch!])

        handle-delete
        (uix/use-callback
         (fn [entry-id]
           (when collection-field
             (delete-note! agg-key record-id collection-field entry-id
                           (fn []
                             (do-fetch!)
                             (rf/dispatch [::agg.events/get agg-key record-id])))))
         [agg-key record-id collection-field do-fetch!])]

    ;; Fetch history on mount, when record-id changes, or after FSM transition
    ;; (state/updated-at change triggers re-fetch so new transition entries appear)
    (uix/use-effect
     (fn []
       (when record-id (do-fetch!))
       js/undefined)
     [record-id record-state record-updated do-fetch!])

    (let [content
          (cond
            loading?
            ($ ui/loader {:size "sm"})

            (empty? entries)
            ($ ui/text {:size "xs" :c "dimmed"} "No activity yet")

            :else
            ($ ui/stack {:gap 2}
              (for [entry entries]
                (let [k (or (:id entry)
                            (str (:event entry) "-" (:occurred_at entry)))]
                  (if (= "note" (:type entry))
                    ($ note-entry
                      {:key k
                       :entry entry
                       :on-delete (when collection-field handle-delete)})
                    ($ transition-entry
                      {:key k
                       :entry entry}))))))]

      ($ ui/stack {:gap "sm"}
        ($ ui/text {:fw 600 :size "sm"} "Activity")

        ;; Add note form (only if collection-field is set)
        (when collection-field
          ($ :<>
            ($ ui/textarea
              {:placeholder "Add a note..."
               :value text
               :onChange #(set-text! (.. % -target -value))
               :minRows 2
               :autosize true
               :size "sm"})
            ($ ui/button
              {:size "compact-xs" :variant "light" :fullWidth true
               :onClick handle-add
               :loading adding?
               :disabled (empty? (.trim text))}
              "Add Note")))

        ;; Timeline
        content))))
